import { Component, HostBinding, OnInit, inject } from '@angular/core';
import { ApiPage, ApiSlug, ApiUser, PagesApi, RouterApi } from '@tytapp/api';
import { Base64, Cache, OpenGraph, PageComponent, sleep } from '@tytapp/common';
import { ContentBlock, ContentBlocksService } from '@tytapp/content';
import { UserService } from '@tytapp/user';
import { DEFAULT_HOME_CONTENT } from '../default-home-content';
import { firstValueFrom } from 'rxjs';
import { isClientSide } from '@tytapp/environment-utils';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss']
})
export class HomeComponent extends PageComponent implements OnInit {
    private userService = inject(UserService);
    private opengraph = inject(OpenGraph);
    private pagesApi = inject(PagesApi);
    private routerApi = inject(RouterApi);
    private base64 = inject(Base64);
    private contentBlocks = inject(ContentBlocksService);

    page: ApiPage;

    get pageComponentId() {
        return 'home';
    }

    get blocks() {
        return this.page?.blocks as ContentBlock[];
    }

    @HostBinding('class.animate-in')
    animateIn = false;

    user: ApiUser;
    state: 'loading' | 'ready' | 'failed' = 'loading';
    errorMessage: string;
    showEditControls = true;
    staticContent = false;
    hasPermissionToEdit = false;

    markReady: () => void;
    ready = new Promise<void>((res) => this.markReady = res);

    async init() {
        this.pageTitle = 'Home';
        this.head.setCanonicalLink('/');
        this.opengraph.setLdJson({
            "@context": "http://schema.org",
            "@type": "Organization",
            "name": "The Young Turks",
            "url": "https://tyt.com",
            "sameAs": [
                "https://www.facebook.com/TheYoungTurks",
                "https://twitter.com/TheYoungTurks",
                "http://www.youtube.com/theyoungturks",
                "https://www.linkedin.com/company/the-young-turks",
                "http://tytnetwork.tumblr.com/",
                "http://www.pinterest.com/tytnetwork",
                "http://instagram.com/tytnetwork",
                "https://plus.google.com/+TheYoungTurks"]
        });

        await this.fetchHomePage();
    }

    pageBuilderUrl = '/home';

    get createPageUrl() {

        let query = new URLSearchParams();
        query.set('url', this.pageBuilderUrl);
        query.set('title', `Home`);
        query.set('description', 'TYT.com Home Page');
        if (this.page)
            query.set('blocks', this.base64.encode(JSON.stringify(this.page.blocks)));

        return `/pages/edit/new?${query.toString()}`;

    }

    private homePageCache = Cache.shared<ApiPage>('home-page-content', { timeToLive: 1000 * 60 * 15, maxItems: 1 });

    private async fetchHomePage() {
        this.state = 'loading';
        let route: ApiSlug;
        const url = this.pageBuilderUrl;

        let page: ApiPage;

        try {
            page = await this.homePageCache.fetch('page', async () => {
                route = await this.routerApi.getRoute(url).toPromise();
                page = await this.pagesApi.getPage(route.uuid).toPromise();
                return page;
            });
        } catch (e) {
            this.logger.error(`Failed to load home page`, e);
            page = undefined;
        }

        if (!page) {
            console.warn(`Failed to locate dynamic home page, falling back to static home page...`);
            page = DEFAULT_HOME_CONTENT;
            this.staticContent = true;
        }

        // Preload all block views and (in some cases) API calls
        await this.contentBlocks.preloadAllBlockViews(page.blocks as ContentBlock[]);
        this.page = page;

        // Wait for the content view to tell us it's ready to be shown.
        await this.ready;

        // There is some amount of structural work left to be done by Angular to get the blocks ready. This can be
        // observed by looking at the time gap between when the 'loading' spinner disappears and when the blocks start
        // to render when this 500ms delay is removed. It sucks that we can't get an event to know when rendering is
        // finished, but it's a smoother user experience.
        // * This also has the effect of letting more API calls finish, leading to more of an "instant start" experience
        //   instead of seeing a lot of pop-in as placeholders get swapped out for real data.
        if (isClientSide())
            await sleep(500);

        this.state = 'ready';

        // Post Load: Wait for user to be ready so we can show the editor bar

        this.user = await firstValueFrom(this.userService.userChanged);

        if (this.user?.staff) {
            this.hasPermissionToEdit = await this.userService.hasPermission({
                action: 'update',
                subject_type: 'CMS::Page',
                subject_cfid: this.page.id
            });
        } else {
            this.hasPermissionToEdit = false;
        }

    }

}
